const hexToRgb = (hex: string) => {
  // Remove the '#' if it exists
  const cleanHex = hex.replace("#", "");

  // Parse the r, g, b values
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return { r, g, b };
};

// Utility function to convert RGB back to hex
const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (component: number) => component.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Function to lighten a hex color by a given percentage
const lightenHexColor = (hex: string, percent: number) => {
  const { r, g, b } = hexToRgb(hex);

  // Increase RGB values by the given percentage, ensuring values stay within the 0-255 range
  const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  return rgbToHex(newR, newG, newB);
};

// WCAG relative luminance, then the contrast ratio between two colours. Needed
// because "the brand colour" is not a usable text colour: the fleet runs from
// #1d191f to #2ecaff, and brand-on-white ranges from 15:1 down to 1.9:1.
const luminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

// WCAG AA for text below 18px. The one number both of the rules below answer to.
const AA_TEXT = 4.5;

const contrastOnWhite = (hex: string) => 1.05 / (luminance(hex) + 0.05);

const contrastRatio = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * The brand colour, darkened just enough to be readable as text on white.
 *
 * Returned unchanged when it already passes WCAG AA (4.5:1), so dark brands keep
 * their exact hue. Light ones are walked down in 4% steps until they pass, which
 * preserves the hue and only spends saturation/brightness — a hand-picked
 * neutral would have been safe but would have thrown the brand away on every
 * light-branded hotel.
 *
 * Measured on the real fleet: #2ecaff (Moom) sat at 1.9:1 and #7d989f (Piedra,
 * Travè) at 2.9:1, i.e. the pill label was somewhere between washed out and
 * invisible. That was a live accessibility defect, not a matter of taste.
 */
const inkOnWhite = (hex: string) => {
  let current = hex;
  // 40 steps of 4% reaches near-black from any starting colour; the loop is
  // bounded so a malformed hex cannot spin.
  for (let i = 0; i < 40 && contrastOnWhite(current) < AA_TEXT; i++) {
    const { r, g, b } = hexToRgb(current);
    current = rgbToHex(
      Math.floor(r * 0.96),
      Math.floor(g * 0.96),
      Math.floor(b * 0.96)
    );
  }
  return current;
};

/**
 * Fill for a solid control that sits INSIDE the assistant's bubble, with white
 * text on it. Two things have to be true at once, and on some brands they pull
 * in opposite directions:
 *
 *   1. white text on the fill passes WCAG AA (4.5:1), and
 *   2. the fill is visibly a separate object from the bubble it sits on.
 *
 * The bubble is `lightenHexColor(brand, 5)`, i.e. essentially the brand itself,
 * so `inkOnWhite` alone is not enough: it stops as soon as (1) holds, and for a
 * brand that already passed (1) unchanged it returns the brand — a chip the
 * exact colour of the bubble it sits on. Measured on PianoB's #33707f, where
 * ink === brand: the chip disappeared.
 *
 * So keep darkening past (1) until (2) holds too. Very dark brands have no room
 * left below them (#1d191f cannot get 1.6:1 away from its own bubble even at
 * pure black), and there the separation has to come from ABOVE: a chip lighter
 * than the bubble, which is what a dark UI does anyway. The direction flips, the
 * contract does not.
 */
const SEPARATION = 1.6;

const chipOnBubble = (hex: string) => {
  const bubble = lightenHexColor(hex, 5);
  const holds = (candidate: string) =>
    contrastOnWhite(candidate) >= AA_TEXT &&
    contrastRatio(candidate, bubble) >= SEPARATION;

  // Down first: a dark chip is the one that reads as a control. 40 steps of 4%
  // reaches near-black from any starting colour; bounded so a malformed hex
  // cannot spin.
  let down = inkOnWhite(hex);
  for (let i = 0; i < 40; i++) {
    if (holds(down)) return down;
    const { r, g, b } = hexToRgb(down);
    down = rgbToHex(
      Math.floor(r * 0.96),
      Math.floor(g * 0.96),
      Math.floor(b * 0.96)
    );
  }

  // Nothing below the bubble is far enough away, so lift the chip above it. Only
  // bubbles darker than ~0.03 luminance land here, which leaves the whole range
  // between SEPARATION and the AA ceiling free — the loop always exits on the
  // first branch, and the sweep in Colors.test.ts is what proves it.
  let up = bubble;
  for (let i = 0; i < 40; i++) {
    up = lightenHexColor(up, 8);
    if (holds(up)) return up;
  }
  return up;
};

// Set every brand-derived CSS custom property in one place. Custom
// properties inherit into the shadow root, so setting them on the
// document root covers both standalone and embedded modes.
const applyBrandTokens = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const root = document.documentElement.style;
  root.setProperty("--primary-color", hex);
  root.setProperty("--primary-rgb", `${r}, ${g}, ${b}`);
  root.setProperty("--primary-soft", lightenHexColor(hex, 92));
  root.setProperty("--primary-tint", lightenHexColor(hex, 75));
  // Brand-coloured TEXT on a white fill. Never use --primary-color for that.
  root.setProperty("--primary-ink", inkOnWhite(hex));
  // Solid FILL for a control inside the assistant's bubble, white text on it.
  root.setProperty("--chip-fill", chipOnBubble(hex));
};

const colors = {
  lightenHexColor,
  inkOnWhite,
  chipOnBubble,
  contrastOnWhite,
  contrastRatio,
  applyBrandTokens,
};

export default colors;
